/**
 * Layout Context - Navigation State Management
 *
 * This context manages the sidebar visibility state across the application.
 * It provides separate state for desktop (collapsible) and mobile (hamburger overlay).
 *
 * Desktop Behavior:
 * - isDesktopSidebarOpen: true = full width (w-64), false = icon-only (w-20)
 * - Toggleable via collapse button in sidebar
 * - Persists across navigation
 *
 * Mobile Behavior:
 * - isMobileSidebarOpen: true = overlay sidebar visible, false = hidden
 * - Toggleable via hamburger menu in header
 * - Includes backdrop overlay when open
 */

import { createContext, useContext, useState, ReactNode } from 'react';

interface LayoutContextType {
  // Desktop collapsible state
  isDesktopSidebarOpen: boolean;
  toggleDesktopSidebar: () => void;
  setDesktopSidebarOpen: (isOpen: boolean) => void;

  // Mobile hamburger state
  isMobileSidebarOpen: boolean;
  toggleMobileSidebar: () => void;
  setMobileSidebarOpen: (isOpen: boolean) => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

interface LayoutProviderProps {
  children: ReactNode;
}

export function LayoutProvider({ children }: LayoutProviderProps) {
  // Desktop: Defaults to open (true)
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);

  // Mobile: Defaults to closed (false)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const toggleDesktopSidebar = () => {
    setIsDesktopSidebarOpen((prev) => !prev);
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen((prev) => !prev);
  };

  const value: LayoutContextType = {
    isDesktopSidebarOpen,
    toggleDesktopSidebar,
    setDesktopSidebarOpen: setIsDesktopSidebarOpen,
    isMobileSidebarOpen,
    toggleMobileSidebar,
    setMobileSidebarOpen: setIsMobileSidebarOpen,
  };

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

export function useLayout() {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
}
